import { useSessionStore, useResultsStore } from '@/store';
import { START_LOCATION } from 'vue-router';

/*
 * At the point the router enters result routes,
 * the session store and any async data should have been fetched.
 *
 * So beforeEnter guards here can and should be synchronous.
 */

export default [
    {
        path: '/results/pending',
        name: 'ResultsPending',
        meta: {
            title: 'Pending review',
        },
        component: () => import('@/pages/results/Pending.vue'),
        beforeEnter: () => {
            const session = useSessionStore();

            if (session.hasConfirmed) {
                return '/results/confirmed';
            }

            if (session.hasSelectedMatch) {
                return '/results/review';
            }

            if (session.allowMatchSelection) {
                if (session.hasSelectedMatch) {
                    return '/results/review';
                } else if (session.hasRecommendedMatches) {
                    return '/results/recommended';
                } else {
                    return '/results/organic';
                }
            }

            return;
        },
    },
    {
        path: '/results/loading',
        name: 'Loading',
        meta: {
            title: 'Loading matches',
        },
        component: () => import('@/pages/results/Loading.vue'),
        beforeEnter: () => {
            const session = useSessionStore();

            if (!session.allowMatchSelection) {
                return '/results/pending';
            }

            if (session.hasConfirmed) {
                return '/results/confirmed';
            }

            return;
        },
    },
    {
        path: '/results/recommended',
        name: 'Recommended',
        meta: {
            title: 'Results - Recommended',
        },
        component: () => import('@/pages/results/Recommended.vue'),
        beforeEnter: async (to, from) => {
            const session = useSessionStore();
            const ld = session?.order?.lead_distribution;
            const delivered = ld?.delivered_company_ids ?? [];
            const hasLeadDistributionDeliveries =
                Boolean(ld?.already_distributed) || (Array.isArray(delivered) && delivered.length > 0);
            const canShowLeadDistributionTile =
                session.isLocal === true && ld?.can_opt_in === true && hasLeadDistributionDeliveries !== true;

            if (session.hasConfirmed) {
                return '/results/confirmed';
            }

            if (!session.allowMatchSelection) {
                return '/results/pending';
            }

            // always wait for prefetch so local to interstate switch does not cause issues
            // todo - really the cache should clear when the type switches but this is a quick fix as I found clearing the cache was unreliable
            const results = useResultsStore();
            results.prefetch();

            // takes precedence over other re-routes
            if (from == START_LOCATION && session.hasSelectedMatch) {
                return '/results/review';
            }

            // Verified outside the confirmation window → sellable but can't auto-distribute;
            // send to the generic contact page rather than the results tiles.
            if (session?.order?.phone_verification_window_expired) {
                return '/request-received';
            }

            if (
                !session.hasRecommendedMatches &&
                !canShowLeadDistributionTile &&
                // Already-distributed local customers must stay on
                // /results/recommended so the post-distribution success
                // surface renders. Without this clause they would be bounced
                // to /results/organic and the lead-selling outcome (4
                // partners delivered) would be invisible.
                // See CU-86d2uzj6n / NVVPN.
                !(session.isLocal === true && hasLeadDistributionDeliveries)
            ) {
                return '/results/organic';
            }
        },
    },
    {
        path: '/results/organic',
        name: 'Organic',
        meta: {
            title: 'Results - Organic',
        },
        component: () => import('@/pages/results/Organic.vue'),
        beforeEnter: async (to, from) => {
            const session = useSessionStore();
            const ld = session?.order?.lead_distribution;
            const delivered = ld?.delivered_company_ids ?? [];
            const hasLeadDistributionDeliveries =
                Boolean(ld?.already_distributed) || (Array.isArray(delivered) && delivered.length > 0);
            const canShowLeadDistributionTile =
                session.isLocal === true && ld?.can_opt_in === true && hasLeadDistributionDeliveries !== true;

            if (session.hasConfirmed) {
                return '/results/confirmed';
            }

            if (!session.allowMatchSelection) {
                return '/results/pending';
            }

            // always wait for prefetch so local to interstate switch does not cause issues
            // todo - really the cache should clear when the type switches but this is a quick fix as I found clearing the cache was unreliable
            const results = useResultsStore();
            results.prefetch();

            // takes precedence over other re-routes
            if (from == START_LOCATION && session.hasSelectedMatch) {
                return '/results/review';
            }

            // Already-distributed local customers belong on /results/recommended
            // (post-distribution success surface) regardless of how they got
            // here — Loading.vue can race and route to organic when its
            // session.retrieve() returns the pre-distribution payload by ~1s
            // (CU-86d2uzj6n / W78YN audit). Bounce on every entry, not just
            // initial load.
            if (session.isLocal === true && hasLeadDistributionDeliveries) {
                return '/results/recommended';
            }

            // Verified outside the confirmation window → sellable but can't auto-distribute;
            // send to the generic contact page rather than organic matches.
            if (session?.order?.phone_verification_window_expired) {
                return '/request-received';
            }

            // if initial load and had recommended matches, go to recommended
            if (from == START_LOCATION && (session.hasRecommendedMatches || canShowLeadDistributionTile)) {
                return '/results/recommended';
            }
        },
    },
    {
        path: '/results/review',
        name: 'Review',
        meta: {
            title: 'Review',
        },
        component: () => import('@/pages/results/Review.vue'),
        beforeEnter: () => {
            const session = useSessionStore();

            if (session.hasConfirmed) {
                return '/results/confirmed';
            } else {
                if (!session.allowMatchSelection && !session.hasSelectedMatch) {
                    return '/results/pending';
                } else if (session.hasSelectedMatch) {
                    return;
                } else {
                    return '/results/organic';
                }
            }
        },
    },
    {
        path: '/results/confirmed',
        name: 'Confirmed',
        meta: {
            title: 'Confirmed',
        },
        component: () => import('@/pages/results/Confirmed.vue'),
        beforeEnter: () => {
            const session = useSessionStore();

            if (session.hasConfirmedLongAgo) {
                return '/returning';
            } else if (session.hasConfirmed) {
                return;
            } else {
                if (session.hasSelectedMatch) {
                    return '/results/review';
                } else {
                    return '/results/recommended';
                }
            }
        },
    },
];
